import { innerSearch } from 'api/Api';
import LoadingIndicator from 'app/components/LoadingIndicator';
import LoadingText from 'app/components/LoadingText';
import PayverseDirectHeader from 'app/components/PayverseDirectHeader';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { findImage } from 'utils/Utils';

interface ReturnForm {
  resultStatus: string;
  resultMessage: string;
  resultCode: string;
  tid: string;
  orderId: string;
  mallReserved: string;
}

const DirectPaymentPage: React.FC = () => {
  const searchParams = useLocation().search;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setPaymentState } = usePaymentState();

  const [tid, setTid] = useState<string>('');
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [type, setType] = useState<string>('APM');
  const [isPopupBlocked, setIsPopupBlocked] = useState<boolean>(false);

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(tid), {
    onSuccess: data => {
      // returnUrl Set
      const returnUrl = data.data.returnUrl;
      // 결제수단 Set
      const payType = String(data.data.display.payType).toLowerCase();
      const response = data.data.response;

      setPaymentState(prevState => ({
        ...prevState,
        resultMessage: data.data.response.resultMessage,
        resultCode: data.data.response.resultCode,
        tid: data.data.response.tid,
        orderId: data.data.response.orderId,
        returnUrl: data.data.returnUrl,
        descriptor: data.data.display.descriptor,
      }));

      // 상태코드 예외처리
      if (
        data.data.response.resultStatus === null ||
        data.data.response.resultStatus === undefined
      ) {
        navigate('/error');
      }
      if (
        (String(response.resultStatus).toLowerCase() === 'pending' &&
          payType !== 'vbank') ||
        (String(response.resultStatus).toLowerCase() === 'pending' &&
          payType !== 'otc')
      ) {
        navigate('/directPending');
      } else if (
        String(response.resultStatus).toLocaleLowerCase() === 'processing'
      ) {
        navigate('/directPending');
      } else {
        createFormAndSubmit(response, returnUrl);
      }
    },
    onError: error => {
      navigate('/error');
    },
  });

  useEffect(() => {
    const browserTypeJson = (lang: string) => {
      switch (lang) {
        case 'zh-TW':
          return 'cntr';
        case 'zh-CN':
          return 'cnsi';
        case 'en-US':
          return 'en';
        case 'ko':
          return 'ko';
        case 'ko-KR':
          return 'ko';
        case 'ja':
          return 'jp';
        default:
          return '';
      }
    };
    const userLanguage = navigator.language;
    const browserLanguage = browserTypeJson(userLanguage);
    if (browserLanguage !== '') {
      i18n.changeLanguage(browserLanguage);
      return;
    } else {
      i18n.changeLanguage('en');
    }
  }, []);

  useEffect(() => {
    const paramTid = new URLSearchParams(searchParams).get('tid');
    const paramPayUrl = new URLSearchParams(searchParams).get('payUrl');
    const responseType = new URLSearchParams(searchParams).get('responseType');

    // innerSearch를 위한 tid set
    if (paramTid) {
      setTid(paramTid);
      setPaymentState(prevState => ({ ...prevState, tid: paramTid }));
    }

    if (paramPayUrl) {
      setRedirectUrl(paramPayUrl);
    }

    if (responseType) {
      setType(responseType);
    }

    // 결제수단 URL 로직
    if (paramPayUrl === null) {
      navigate('/error');
    } else {
      if (isMobile) {
        window.location.replace(`${paramPayUrl}`);
      } else {
        openPopup(paramPayUrl);
      }
    }
  }, [searchParams]);

  const createFormAndSubmit = (formData, returnUrl) => {
    const form = document.createElement('form');
    form.setAttribute('method', 'POST');
    form.setAttribute('action', `${returnUrl}`);
    form.style.display = 'none';
    Object.entries(formData).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', String(value));
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  const openPopup = (url: string) => {
    const popupWindow = window.open(`${url}`, '_blank', 'width=700,height=650');

    if (
      !popupWindow ||
      popupWindow.closed ||
      typeof popupWindow.closed === 'undefined'
    ) {
      setIsPopupBlocked(true);
    } else {
      setIsPopupBlocked(false);
      const checkPopupClosed = setInterval(() => {
        if (popupWindow.closed) {
          clearInterval(checkPopupClosed);
          handlePopupClose();
        }
      }, 1000);
    }

    //   if (popupWindow) {
    //     const checkPopupClosed = setInterval(() => {
    //       if (popupWindow.closed) {
    //         clearInterval(checkPopupClosed);
    //         handlePopupClose();
    //       }
    //     }, 1000);
    //   }
  };

  const handlePopupClose = () => {
    transactResult.mutate();
  };

  const handleClickOpenPopup = (url: string) => {
    setIsPopupBlocked(false);
    const popupWindow = window.open(
      `${redirectUrl}`,
      '_blank',
      'width=700,height=650',
    );
    if (popupWindow) {
      const checkPopupClosed = setInterval(() => {
        if (popupWindow.closed) {
          clearInterval(checkPopupClosed);
          handlePopupClose();
        }
      }, 1000);
    }
  };

  return (
    <>
      {isPopupBlocked ? (
        <div className="payverse-wrap">
          <div className="payverse">
            <PayverseDirectHeader />
            <div className="completed">
              <div className="middle">
                <div className="icon">
                  <img src={findImage('error.png')} />
                </div>
                <div className="title">{t('popupBlocked')}</div>
                <div className="text" style={{ whiteSpace: 'pre-line' }}>
                  {t(`allowPopup`)}
                </div>
              </div>
            </div>
            <div className="completed-btn">
              <div className="box confirmBox">
                <button
                  className="all-close"
                  onClick={() => handleClickOpenPopup(redirectUrl)}
                >
                  {t('openPopup')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="payverse-wrap">
          <div className="payverse">
            <PayverseDirectHeader />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                height: '70%',
              }}
            >
              <LoadingIndicator />
              <LoadingText />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DirectPaymentPage;
